<template>
  <base-card>
    <div class="tw-flex tw-flex-col">
      <h3 class="tw-text-gray-700 tw-text-lg tw-font-thin tw-whitespace-nowrap tw-mb-1">Monthly Recurring Revenue <span class="tw-text-sm">(MRR)</span></h3>
      <div class="tw-text-gray-700 tw-text-lg tw-font-normal tw-whitespace-nowrap tw-mb-2 tw-self-center">
        {{ formattedTotalRevenueInCents }}
      </div>
      <div class="chart-container tw-self-center">
        <div v-if="isLoading" class="tw-bg-gray-200 tw-animate-pulse tw-rounded-full tw-h-80 tw-w-80" />
        <doughnut-chart v-else :chart-data="revenueGroupChartData" :options="chartOptions" style="height: 20rem" />
      </div>
    </div>
  </base-card>
</template>

<script>
const formatter = new Intl.NumberFormat(window.navigator.language, {
  style: "currency",
  currency: "USD",
});

import DoughnutChart from "@/components/charts/DoughnutChart";
import { mapGetters } from "vuex";

export default {
  name: "MonthlyRevenue",
  components: { DoughnutChart },
  props: {
    businessId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      groupRevenue: [],
      totalRevenueInCents: 0,
      currency: "usd",
      isLoading: false,
      windowWidth: window.innerWidth,
    };
  },
  computed: {
    formattedTotalRevenueInCents() {
      return formatter.format(this.totalRevenueInCents / 100);
    },
    ...mapGetters("organization", ["organization", "cobrandId"]),
    isMobile() {
      return this.windowWidth <= 768;
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        cutoutPercentage: 70,
        legend: {
          display: this.isMobile === true ? false : true,
          position: "bottom",
        },
        tooltips: {
          callbacks: {
            label(tooltipItem, data) {
              let label = data.labels[tooltipItem.index] || "";

              if (label) {
                label += ": ";
              }
              label += `${formatter.format(data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index])}`;
              return label;
            },
          },
        },
      };
    },

    revenueGroupChartData() {
      if (this.groupRevenue.length == 0)
        return {
          labels: ["No revenue"],
          datasets: [
            {
              label: "Plan Group",
              data: [1],
              borderColor: "#cccccc", // secondary
              borderWidth: 0.5,
              backgroundColor: ["#ffffff"],
            },
          ],
        };
      else
        return {
          labels: this.groupRevenue.map((groupRevenue) => groupRevenue.group),
          datasets: [
            {
              label: "Plan Group",
              data: this.groupRevenue.map((groupRevenue) => groupRevenue.revenueInCents / 100),
              borderColor: this.groupBorderColor, // secondary
              borderWidth: 0.5,
              backgroundColor: this.groupBackgroundColors,
            },
          ],
        };
    },
    groupBorderColor() {
      switch (this.cobrandId) {
        case "rx-harmony":
          return "#49a13e";
      }
      return "#522e91";
    },
    groupBackgroundColors() {
      switch (this.cobrandId) {
        case "rx-harmony":
          return [
            "rgba(85, 185, 72, 0.8)",
            "rgba(85, 185, 72, 0.6)",
            "rgba(85, 185, 72, 0.3)",
            "rgba(85, 185, 72, 0.0)",
            "#cccccc",
            "rgba(85, 185, 72, 0.8)",
            "rgba(85, 185, 72, 0.6)",
            "rgba(85, 185, 72, 0.3)",
            "rgba(85, 185, 72, 0.0)",
            "#cccccc",
          ];
      }
      return ["#b9abd3", "#ff99d2", "#522e91", "#ff59d7", "#cccccc", "#b9abd3", "#ff99d2", "#522e91", "#ff59d7", "#cccccc"];
    },
  },
  created() {
    this.loadMonthlyRevenue();
    window.addEventListener("resize", this.onResize);
  },
  destroyed() {
    window.removeEventListener("resize", this.onResize);
  },
  methods: {
    loadMonthlyRevenue() {
      this.isLoading = true;

      let url = `/businesses/${this.businessId}/dashboard/monthly-revenue`;

      this.$axios
        .get(url)
        .then((res) => {
          this.groupRevenue = res.data.results;
          this.totalRevenueInCents = res.data.totalRevenueInCents;
          this.currency = res.data.currency;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    onResize() {
      this.windowWidth = window.innerWidth;
    },
  },
};
</script>
